/**
 * OverlayBongDisplay - Bong/betting slip with broadcast theme
 * Supports dual bong display (tipsekspert + guest) side by side
 * Colors adapt to Sommertravet mode via CSS variables
 */

function BongColumn({ bongData, bongSelectedLegs }) {
  if (!bongData) return null;
  const selectedSet = bongSelectedLegs && bongSelectedLegs.length > 0 
    ? new Set(bongSelectedLegs) : null;
  const ownerName = bongData.ownerName || 'Bong';

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Column header with name */}
      <div style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        fontSize: '1rem', fontWeight: 700, color: 'var(--theme-accent)',
        padding: '8px 13px',
        borderBottom: '1px solid var(--theme-panel-border-subtle)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
        textAlign: 'center',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {ownerName}
      </div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", padding: '8px 13px' }}>
        {Object.entries(bongData.legs || {})
          .filter(([leg]) => !selectedSet || selectedSet.has(parseInt(leg)))
          .map(([leg, horses]) => {
          const hasSplit = horses?.includes('/');
          const horseList = horses?.split(',').map(h => h.trim()).filter(h => h) || [];
          const isBanker = !hasSplit && horseList.length === 1;
          let displayText;
          if (horses?.includes('/')) {
            const [left, right] = horses.split('/');
            const rightHorses = right.split(',').filter(h => h.trim());
            const totalHorseCount = bongData.totalHorsesPerLeg?.[leg] || 0;
            const leftCount = left.split(',').filter(h => h.trim()).length;
            if (totalHorseCount > 0 && (leftCount + rightHorses.length) >= totalHorseCount) {
              displayText = `${left.trim()} / Alle`;
            } else {
              displayText = `${left.trim()} / ${right.trim()}`;
            }
          } else {
            displayText = horses;
          }
          
          return horses && (
            <div 
              key={leg}
              className="broadcast-row"
              style={{
                padding: '6px 0',
                borderBottom: '1px solid var(--theme-panel-border-faint)',
                fontSize: '1.05rem', color: '#FFFFFF',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: 'var(--theme-accent)', fontSize: '1rem' }}>{bongData.gameType}-{leg}:</span>
                <span style={{ fontSize: '1rem' }}>
                  {displayText}
                  {isBanker && <span style={{ color: 'var(--theme-accent)', marginLeft: '6px', fontWeight: 700 }}>B</span>}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Cost */}
      {!selectedSet && (
        <div style={{
          padding: '9px 16px',
          borderTop: '1px solid var(--theme-panel-border-subtle)',
          display: 'flex', justifyContent: 'space-between',
          fontFamily: "'JetBrains Mono', monospace", fontSize: '1.05rem',
          fontWeight: 700, color: 'var(--theme-accent)'
        }}>
          <span>Kr:</span>
          <span>{bongData.hasSplit ? `${bongData.cost?.toFixed(0)} / ${bongData.costLarge?.toFixed(0)}` : `${bongData.cost?.toFixed(0)}`} kr</span>
        </div>
      )}
    </div>
  );
}

import { TravparkLogo } from './TravparkLogo';

export function OverlayBongDisplay({
  bongVisible, showBong, bongData, bongDataGuest, bongSelectedLegs, colors, sommertravetMode
}) {
  if (!bongVisible) return null;
  const hasBoth = bongData && bongDataGuest;

  return (
    <div
      data-testid="bong-display"
      className={showBong && bongData ? 'broadcast-slide-left' : 'broadcast-slide-left-out'}
      style={{
        position: 'absolute',
        left: '60px',
        bottom: '85px',
        backgroundColor: 'var(--theme-panel-bg)',
        borderRadius: '10px',
        border: '2px solid var(--theme-panel-border)',
        padding: '0',
        minWidth: hasBoth ? '500px' : '290px',
        boxShadow: `0 0 30px rgba(var(--theme-accent-rgb), 0.15), 0 15px 45px rgba(0,0,0,0.5)`,
        zIndex: 1,
        overflow: 'hidden',
        isolation: 'isolate',
      }}
    >
      {bongData && (
        <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Shared header */}
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: '1.4rem', fontWeight: 700, color: 'var(--theme-accent)',
            backgroundColor: '#050F23',
            backgroundImage: `linear-gradient(135deg, var(--theme-header-gradient) 0%, transparent 100%)`,
            borderBottom: '2px solid var(--theme-panel-border)',
            textTransform: 'uppercase',
            padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <TravparkLogo height={52} sommertravetMode={sommertravetMode} />
            <span>{bongData.gameType} Bong</span>
          </div>

          {/* Bong columns */}
          <div style={{ display: 'flex' }}>
            <BongColumn bongData={bongData} bongSelectedLegs={bongSelectedLegs} />
            {hasBoth && (
              <>
                <div style={{ width: '2px', backgroundColor: 'var(--theme-panel-border-subtle)', flexShrink: 0 }} />
                <BongColumn bongData={bongDataGuest} bongSelectedLegs={bongSelectedLegs} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
